import AnnotationQuestion from "@uui/AnnotationQuestion";

import CloseIcon from "@mui/icons-material/Close";
import { Card, IconButton, Modal, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";

import { MeetingFeedbackSummary } from "@app/analytics/types";
import { pluralize, pluralizePhrase } from "@app/common/utils";

import Stack, { Column, Row, StackProps } from "@rui/Stack";

const StyledCard = styled(Card)(({ theme }) => ({
  position: "absolute",
  top: "20px",
  left: "50%",
  transform: "translate(-50%)",
  maxHeight: "95%",
  width: "400px",
  maxWidth: "90vw",
  overflowY: "scroll",
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    top: "50%",
    transform: "translate(-50%, -50%)",
    minWidth: "400px",
  },
}));

const FeedbackSummary = styled((props: StackProps) => (
  <Row variant="apart" {...props} />
))(({ theme }) => ({
  padding: theme.spacing(1),
  color: theme.palette.shades.neutral[500],
  borderRadius: "4px",
  flex: 1,
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

export const PendingIcon = styled(AnnotationQuestion)(({ theme }) => ({
  height: "16px",
  width: "16px",
  color: theme.palette.shades.neutral[200],
}));

const MeetingReportFeedbackSummaryModal = ({
  feedback,
  open,
  onClose,
}: {
  feedback: MeetingFeedbackSummary;
  open: boolean;
  onClose: () => void;
}) => {
  if (feedback == null) return null;

  const numMissingResponses = feedback.pending + feedback.skipped;

  return (
    <Modal open={open} disableAutoFocus onClose={onClose}>
      <StyledCard>
        <Column spacing={2}>
          <Row variant="apart">
            <Typography variant="h2">Feedback</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Row>
          <Typography
            variant="bodySecondaryReg"
            sx={{ color: "shades.neutral.700", pb: 1 }}
          >
            All attendees are asked to provide feedback after a meeting. Results
            are only visible to the meeting host.
          </Typography>
          <Typography variant="bodyPrimaryMed">
            Was this meeting a good use of your time?
          </Typography>
          <Stack
            variant="apart"
            direction={{ xxs: "column", sm: "row" }}
            spacing={{ xxs: 1.5, sm: 5 }}
            flex={1}
          >
            <FeedbackSummary>
              <span>👍 Yes</span>
              <span>{feedback.positive}</span>
            </FeedbackSummary>
            <FeedbackSummary>
              <span>👎 No</span>
              <span>{feedback.negative}</span>
            </FeedbackSummary>
          </Stack>
          {numMissingResponses > 0 && (
            <Row sx={{ px: 0.5 }} variant="center" spacing={1}>
              <PendingIcon />
              <Typography
                variant="bodySecondaryReg"
                sx={{ color: "shades.neutral.500" }}
              >
                {pluralizePhrase(numMissingResponses, "attendee")}{" "}
                {pluralize("hasn't", "haven't", numMissingResponses)} responded
              </Typography>
            </Row>
          )}
        </Column>
      </StyledCard>
    </Modal>
  );
};

export default MeetingReportFeedbackSummaryModal;
